// Most Overdue section — FE-033 / spec §7.0.3.
//
// Top 5 staff by overdue Commitment count. Each row prefers the
// backend-supplied ``drill_through_url`` so the URL stays in sync
// with the backend's filter contract; if the field is empty the
// component falls back to ``buildCommitmentFilterUrl`` with the
// dashboard's current date range.

import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

import {
  buildCommitmentFilterUrl,
  DashboardCard,
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
  useCommonFilters,
  useDashboardDateRange,
  useMostOverdueQuery,
  type DashboardDateRange,
  type MostOverdueRow,
} from "@/features/dashboard";

import { formatStaffName } from "./displayName";


export const MOST_OVERDUE_SECTION = "most-overdue" as const;


export function MostOverdueSection() {
  const filters = useCommonFilters();
  const dateRange = useDashboardDateRange();
  const query = useMostOverdueQuery(filters);
  return (
    <DashboardCard
      section={MOST_OVERDUE_SECTION}
      title="Most overdue"
      subtitle="Staff with the most overdue Commitments"
    >
      {query.isLoading ? (
        <DashboardLoadingState variant="list" rows={5} />
      ) : query.isError ? (
        <DashboardErrorState
          title="Couldn't load Most overdue"
          retry={() => query.refetch()}
        />
      ) : query.data && query.data.rows.length > 0 ? (
        <ol
          data-testid="most-overdue-rows"
          className="flex flex-col gap-2"
        >
          {query.data.rows.map((row, idx) => (
            <MostOverdueRowView
              key={row.user_id}
              row={row}
              rank={idx + 1}
              dateRange={dateRange}
            />
          ))}
        </ol>
      ) : (
        <DashboardEmptyState
          title="No overdue Commitments right now"
          description="Once Commitments slip past their due dates, the staff carrying them appear here."
        />
      )}
    </DashboardCard>
  );
}


function MostOverdueRowView({
  row, rank, dateRange,
}: {
  row: MostOverdueRow;
  rank: number;
  dateRange: DashboardDateRange;
}) {
  const href = row.drill_through_url || fallbackHref(row.user_id, dateRange);
  const name = formatStaffName(row);
  const oldestDue = row.oldest_due_date
    ? formatRelativeDate(row.oldest_due_date)
    : null;
  return (
    <li>
      <Link
        to={href}
        data-testid={`most-overdue-row-${row.user_id}`}
        aria-label={
          `${name}: ${row.overdue_count} overdue ` +
          `of ${row.total_open_count} open Commitments`
        }
        className="flex items-center gap-3 rounded-md bg-recessed px-3 py-2 transition-fast ease-out hover:bg-raised"
      >
        <span
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-raised text-xs font-medium text-text-secondary"
          aria-hidden="true"
        >
          {rank}
        </span>
        <span className="flex flex-1 flex-col">
          <span className="truncate text-sm font-medium text-text-primary">
            {name}
          </span>
          <span className="text-xs text-text-tertiary">
            {row.overdue_count} overdue · {row.total_open_count} open
            {oldestDue ? ` · oldest due ${oldestDue}` : ""}
          </span>
        </span>
        <span
          className="text-sm font-semibold text-ember"
          data-testid={`most-overdue-count-${row.user_id}`}
          aria-hidden="true"
        >
          {row.overdue_count}
        </span>
        <ChevronRight
          size={14}
          className="text-text-tertiary"
          aria-hidden="true"
        />
      </Link>
    </li>
  );
}


function fallbackHref(userId: string, dateRange: DashboardDateRange): string {
  return buildCommitmentFilterUrl(
    { assigned_to: userId, overdue: true },
    dateRange,
  );
}


function formatRelativeDate(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    month: "short", day: "numeric",
  });
}
